/**
 * MobileTodoSheet Component
 * 
 * Header overlay for viewing and managing todos on mobile.
 * Expands DOWN from header when todo row is tapped.
 */

import React, { useState, useRef, useCallback } from 'react';
import { X, Plus, Check, Circle, Trash2 } from 'lucide-react';
import { fireConfetti } from '@/components/spaces/JournalTodoList';
import type { TodoItem } from '@/types/journal';
import './mobile.css';

interface MobileTodoSheetProps {
    isOpen: boolean;
    onClose: () => void;
    todos: TodoItem[];
    onToggle: (id: string) => void;
    onAdd: (text: string) => void;
    onDelete: (id: string) => void;
}

const MobileTodoSheet: React.FC<MobileTodoSheetProps> = ({
    isOpen,
    onClose,
    todos,
    onToggle,
    onAdd,
    onDelete,
}) => {
    const [newTodoText, setNewTodoText] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const activeTodos = todos.filter(t => !t.completed).sort((a, b) => a.order - b.order);
    const completedTodos = todos.filter(t => t.completed);
    const [showCompleted, setShowCompleted] = useState(false);

    const handleToggle = useCallback((id: string) => {
        const todo = todos.find(t => t.id === id);
        const wasCompleted = todo?.completed;
        onToggle(id);

        // Fire confetti when marking as complete
        if (!wasCompleted) {
            fireConfetti();
        }
    }, [todos, onToggle]);

    const handleAdd = useCallback(() => {
        if (!newTodoText.trim()) return;
        onAdd(newTodoText.trim());
        setNewTodoText('');
        inputRef.current?.focus();
    }, [newTodoText, onAdd]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="mobile-todo-sheet-backdrop" onClick={onClose}>
            <div
                className="mobile-todo-sheet"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="mobile-todo-sheet-header">
                    <h2>To Do</h2>
                    <button
                        className="mobile-todo-sheet-close"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Todo list */}
                <div className="mobile-todo-sheet-content">
                    {/* Add new todo */}
                    <div className="mobile-todo-add">
                        <Plus className="w-5 h-5 text-slate-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={newTodoText}
                            onChange={e => setNewTodoText(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Add a task..."
                            className="mobile-todo-add-input"
                        />
                        {newTodoText.trim() && (
                            <button
                                className="mobile-todo-add-btn"
                                onClick={handleAdd}
                            >
                                Add
                            </button>
                        )}
                    </div>

                    {/* Active todos */}
                    {activeTodos.length === 0 && completedTodos.length === 0 ? (
                        <div className="mobile-todo-empty">
                            <p>No tasks yet</p>
                            <span>Add your first task above</span>
                        </div>
                    ) : (
                        <>
                            {activeTodos.map(todo => (
                                <div key={todo.id} className="mobile-todo-item">
                                    <button
                                        className="mobile-todo-checkbox"
                                        onClick={() => handleToggle(todo.id)}
                                    >
                                        <Circle className="w-5 h-5" />
                                    </button>
                                    <span className="mobile-todo-text">{todo.text}</span>
                                    <button
                                        className="mobile-todo-delete"
                                        onClick={() => onDelete(todo.id)}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}

                            {/* Completed section */}
                            {completedTodos.length > 0 && (
                                <div className="mobile-todo-completed-section">
                                    <button
                                        className="mobile-todo-completed-toggle"
                                        onClick={() => setShowCompleted(!showCompleted)}
                                    >
                                        {showCompleted ? '▼' : '▶'} {completedTodos.length} completed
                                    </button>

                                    {showCompleted && completedTodos.map(todo => (
                                        <div key={todo.id} className="mobile-todo-item completed">
                                            <button
                                                className="mobile-todo-checkbox checked"
                                                onClick={() => handleToggle(todo.id)}
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                            <span className="mobile-todo-text">{todo.text}</span>
                                            <button
                                                className="mobile-todo-delete"
                                                onClick={() => onDelete(todo.id)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MobileTodoSheet;
